import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "ngx-store";
import { BehaviorSubject, of } from "rxjs";
import { catchError, map, pluck, switchMap, tap } from "rxjs/operators";
import { DriverTruckPair, DriverTruckPairInput } from "../model/driver-truck-pairing";
import { Page } from "../model/pagination";
import { Url } from "../model/users.model";

@Injectable({
  providedIn: 'root'
})
export class DriverTruckPairingService {
  private url: Url = this.storage.get('url');

  private endpoint = `${this.url.rest}/tms/driver-truck-pairs`;

  private pairsCache: DriverTruckPair[] = [];

  public pairsCache$ = new BehaviorSubject<DriverTruckPair[]>([]);

  public constructor(
    private client : HttpClient,
    private storage: LocalStorageService
  ) {
    this.pairsCache$.subscribe((update) => this.pairsCache = update);
  }

  /**
   * Fetch the page of truck pairs.
   * @param options.perPage Number of driver-truck pairs to 
   * @returns List of truck pairs.
   */
  public getPairs(options: { perPage: number } = { perPage: -1 }) {
    options.perPage = -1; // temporarily make it mandatory to fetch all pairs

    return this.client.get<Page<DriverTruckPair>>(this.endpoint, {
      params: {
        per_page: `${options.perPage}`,
        include: 'fleet'
      }
    })
    .pipe(
      pluck('data'),
      tap((fetchedPairs) => {
        this.pairsCache = fetchedPairs;
        this.pairsCache$.next(this.pairsCache);
      })
    );
  }


  public getPairsv2(){
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }
    return this.client.get(this.url.rest+'/tms/driver-truck-pairsv2', httpOptions);
  }


  /**
   * Get the paird truck of the driver, if there exists.
   * @param id Driver identifier.
   * @returns Paired truck of the driver, if there exists.
   */
  public getPair(id: number) {
    id = Number(id);

    return this.pairsCache$.pipe(
      switchMap((pairList) => {
        const foundIndex = pairList.findIndex((cachedPair) => cachedPair.driver_id === id);

        if(foundIndex !== -1) {
          return of(pairList[foundIndex]);
        }
        else {
          return this.getPairFromServer(id).pipe(
            tap((fetchedPair) => {
              if(fetchedPair) {
                pairList.push(fetchedPair);
                this.pairsCache$.next(pairList);
              }
            })
          );
        }
      })
    );
  }

  /**
   * Save the drvier-truck pairing.
   * @param pair Driver-truck pairing to save.
   * @returns The saved driver-truck pairing, or nothing if not saved.
   */
  public pair(pair: DriverTruckPairInput) {
    pair = {
      driver_id: Number(pair.driver_id),
      fleet_id : Number(pair.fleet_id)
    };

    const identicalFound = this.pairsCache.findIndex((cachedPair) => cachedPair.driver_id === pair.driver_id && cachedPair.fleet_id === pair.fleet_id);

    if(identicalFound !== -1) {
      console.info('The driver-truck pairing is already up to date. Nothing is paired.');
      return of(this.pairsCache[identicalFound]);
    }
    else {
      return this.pairFromServer(pair).pipe(
        tap((savedPair) => {
          if(savedPair) {
            const replacedPair = this.pairsCache.findIndex((cachedPair) => cachedPair.driver_id === savedPair.driver_id);

            if(replacedPair === -1) {
              this.pairsCache.push(savedPair);
              this.pairsCache$.next(this.pairsCache);
            }
            else {
              this.pairsCache.splice(replacedPair, 1);
              this.pairsCache$.next(this.pairsCache);
            }
          }
        })
      );
    }
  }

  /**
   * Remove driver-truck pairing from client cache and the server.
   * @param pair Pair to delete.
   * @returns The removed pair, if there is any.
   */
  public unpair(pair: DriverTruckPairInput) {
    pair = {
      driver_id: Number(pair.driver_id),
      fleet_id : Number(pair.fleet_id)
    };

    const toUnpair = this.pairsCache.findIndex((cachedPair) => cachedPair.driver_id === pair.driver_id);

    if(toUnpair === -1) {
      console.warn('The pair being deleted does not exist in cache. No unpairing is done.');
      return of<null>(null);
    }
    else {
      return this.unpairFromServer(this.pairsCache[toUnpair]).pipe(
        map((unpaired) => {
          if(unpaired) {
            const [removed] = this.pairsCache.splice(toUnpair, 1);
            this.pairsCache$.next(this.pairsCache);
            return removed;
          }
          else {
            return null;
          }
        })
      );
    }
  }

  /**
   * Fetch all driver-truck pairings for the given driver (usually only one pair max).
   * @param id Driver identifier.
   * @returns Truck paired to the driver, or nothing.
   */
  private getPairFromServer(id: number) {
    return this.client.get<DriverTruckPair>(`${this.endpoint}/${id}?include=fleet`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if(error.status === 404) {
            console.info(`No truck is paired to driver ${id}.`);
          }

          return of<null>(null);
        })
      );
  }

  /**
   * Save driver-truck pair to the server.
   * @param pair Pair to be saved.
   * @returns Saved pair information, if saved.
   */
  private pairFromServer(pair: DriverTruckPairInput) {
    return this.client
      .post<DriverTruckPair>(`${this.endpoint}?include=fleet`, pair)
      .pipe(
        tap((savedPair) => console.debug(`Driver ${savedPair.driver_id} is successfully paired to truck ${savedPair.fleet_id} on the server.`)),
        catchError(() => {
          console.error('Driver-truck pair cannot be saved.', pair);
          return of<null>(null);
        })
      );
  }

  /**
   * Delete the driver-truck pair from the server.
   * @param pair The driver-truck pair to delete.
   * @returns The deleted pair from the server, if there is any.
   */
  private unpairFromServer(pair: DriverTruckPair) {
    return this.client
      .delete<DriverTruckPair>(`${this.endpoint}/${pair.driver_id}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.warn(
            error.status === 404
              ? 'The server could not find the driver-truck pair to be deleted.'
              : 'The driver-truck pair could not be deleted from the server.',
            pair
          );
          return of<null>(null)
        })
      );
  }
}
